import React, { useEffect, useState, useRef } from 'react';
import ScrollImageSequence from '../../components/ScrollImageSequence'; // Import new component
import { Title, Text, Button, Container, SimpleGrid, Card, Badge, ThemeIcon, Group as GroupMantine, Accordion } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import { IconRocket, IconCertificate, IconTruck, IconBrush, IconUpload, IconHelp } from '@tabler/icons-react';

// Black Glass Section
const GlassSection = ({ children, style, ...props }) => (
  <div style={{
    padding: '2rem',
    borderRadius: '16px',
    background: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 100px rgba(0, 255, 255, 0.05)',
    color: 'white',
    ...style
  }} {...props}>
    {children}
  </div>
);

// Scroll Snap Container Styles
const containerStyle = {
  height: '100vh',
  overflowY: 'scroll',
  scrollSnapType: 'y mandatory',
  position: 'relative',
  zIndex: 1,
  scrollBehavior: 'smooth'
};

const sectionStyle = {
  minHeight: '100vh',
  width: '100vw',
  display: 'flex',
  alignItems: 'center',
  position: 'relative'
};

export const Home = () => {
  const [activeDrops, setActiveDrops] = useState([]);
  const [featuredReviews, setFeaturedReviews] = useState([]);
  const scrollRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    // Fetch Active Drops
    fetch('https://backend.cdp3d.com/api/drops?status=active')
      .then(res => res.json())
      .then(data => setActiveDrops(data))
      .catch(err => console.error(err));

    // Fetch Featured Reviews
    fetch('https://backend.cdp3d.com/reviews/featured/list')
      .then(res => res.json())
      .then(data => setFeaturedReviews(data))
      .catch(err => console.error("Error fetching featured reviews:", err));
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', position: 'relative' }}>
      {/* Scroll-Synced Image Sequence Background */}
      <ScrollImageSequence frameCount={512} /> {/* Uses window scroll by default */}

      {/* Scroll Snap Main Container - Now flows naturally */}
      <main style={{ ...containerStyle, height: 'auto', overflowY: 'visible', scrollSnapType: 'none' }} ref={scrollRef}>

        {/* HERO SECTION (1) */}
        <section style={{ ...sectionStyle, paddingLeft: isMobile ? '5vw' : '10vw', paddingRight: isMobile ? '5vw' : 0 }}>
          <div style={{ maxWidth: 650 }}>
            <Badge variant="filled" color="dark" size="lg" mb="md" style={{ border: '1px solid #333' }}>The Future of 3D Printing</Badge>
            <Title style={{ fontSize: isMobile ? '3rem' : '4.5rem', color: 'white', lineHeight: 1.1, marginBottom: '1.5rem', letterSpacing: '-2px' }}>
              Transform Ideas <br /> Into <span style={{ color: '#00ffff', textShadow: '0 0 20px rgba(0,255,255,0.5)' }}>Reality</span>
            </Title>
            <Text size="xl" color="white" style={{ opacity: 0.8, maxWidth: '500px' }} mb="xl">
              Design, Upload, Print. <br /> The premium custom 3D printing ecosystem.
            </Text>
            <GroupMantine>
              <Button component={Link} to="/store" size="xl" variant="gradient" gradient={{ from: 'cyan', to: 'indigo' }} style={{ padding: '0 40px' }}>Explore Store</Button>
              <Button component={Link} to="/upload-and-print" size="xl" variant="outline" style={{ borderColor: 'white', color: 'white' }}>Start Printing</Button>
            </GroupMantine>
          </div>
        </section>

        {/* DROPS SECTION (2) */}
        <section style={{ ...sectionStyle, justifyContent: isMobile ? 'center' : 'flex-end', paddingRight: isMobile ? '0' : '10vw', paddingLeft: isMobile ? '0' : 0 }}>
          <GlassSection style={{ width: isMobile ? '90vw' : '45vw', textAlign: isMobile ? 'left' : 'right', margin: isMobile ? '0 auto' : 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
              {/* <Badge color="pink" mb="sm" size="lg">Offering 1</Badge> */}
              <Title order={2} mb="lg" size={isMobile ? '2.5rem' : '3.5rem'} color="white">Exclusive Drops</Title>
              <Text mb="xl" color="white" style={{ opacity: 0.8 }}>Limited edition batch releases.</Text>
            </div>
            {activeDrops.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'row-reverse', gap: '1rem', overflowX: 'auto', paddingBottom: '1rem' }}>
                {activeDrops.map(drop => {
                  const bannerUrl = drop.bannerImage?.startsWith('http') ? drop.bannerImage : `https://backend.cdp3d.com${drop.bannerImage}`;
                  return (
                  <Link to={`/store/drop/${drop._id}`} key={drop._id} style={{ textDecoration: 'none' }}>
                      <div style={{ minWidth: 220, height: 280, borderRadius: 12, backgroundImage: `linear-gradient(to bottom, transparent, black), url(${bannerUrl})`, backgroundSize: 'cover', display: 'flex', alignItems: 'flex-end', padding: 20, border: '1px solid rgba(255,255,255,0.2)' }}>
                      <div><Badge color="red" mb={5}>Live</Badge><Title order={3} size="h5" color="white">{drop.name}</Title></div>
                    </div>
                  </Link>
                  );
                })}
              </div>
            ) : (<Text color="white">No active drops. Stay tuned!</Text>)}
          </GlassSection>
        </section>

        {/* OFFERS SECTION (3) */}
        <section style={{ ...sectionStyle, paddingLeft: isMobile ? 0 : '10vw', justifyContent: isMobile ? 'center' : 'flex-start' }}>
          <GlassSection style={{ width: isMobile ? '90vw' : '45vw' }}>
            {/* <Badge color="orange" mb="sm" size="lg">Offering 2 & 3</Badge> */}
            <Title order={2} mb="xl" color="white" size={isMobile ? '2.5rem' : '3rem'}>Create Your Way</Title>
            <SimpleGrid cols={1} spacing="xl">
              <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <GroupMantine noWrap>
                  <ThemeIcon size={60} radius="md" color="blue" variant="light"><IconUpload size={34} /></ThemeIcon>
                  <div><Title order={3} color="white" mb="xs">Upload & Print</Title><Button component={Link} to="/upload-and-print" variant="white" color="dark" size="xs">Upload File</Button></div>
                </GroupMantine>
              </Card>
              <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <GroupMantine noWrap>
                  <ThemeIcon size={60} radius="md" color="yellow" variant="light"><IconBrush size={34} /></ThemeIcon>
                  <div><Title order={3} color="white" mb="xs">Design Enquiry</Title><Button component={Link} to="/enquiryform" variant="white" color="dark" size="xs">Start Enquiry</Button></div>
                </GroupMantine>
              </Card>
            </SimpleGrid>
          </GlassSection>
        </section>

        {/* FEATURES SECTION (4) */}
        <section style={{ ...sectionStyle, justifyContent: isMobile ? 'center' : 'flex-end', paddingRight: isMobile ? 0 : '10vw' }}>
          <GlassSection style={{ width: isMobile ? '90vw' : '40vw' }}>
            <Title order={2} mb="xl" color="white">Why CDP3D?</Title>
            <SimpleGrid cols={1} spacing="xl">
              {[{ icon: IconRocket, title: 'Precision Engineering', desc: '0.08mm layer' }, { icon: IconTruck, title: 'Global Shipping', desc: 'In-house production' }, { icon: IconCertificate, title: 'Premium Materials', desc: 'PLA, ABS, Resin, TPU, CF Composite' }].map((f, i) => (
                <GroupMantine key={i} noWrap><ThemeIcon size={50} radius="xl" variant="filled" color="dark" style={{ border: '1px solid #333' }}><f.icon size={28} /></ThemeIcon><div><Title order={4} color="white">{f.title}</Title><Text size="sm" color="white" style={{ opacity: 0.7 }}>{f.desc}</Text></div></GroupMantine>
              ))}
            </SimpleGrid>
          </GlassSection>
        </section>

        {/* TESTIMONIALS SECTION (5) */}
        <section style={{ ...sectionStyle, justifyContent: 'center' }}>
          <GlassSection style={{ width: isMobile ? '90vw' : '80vw', textAlign: 'center' }}>
            <Title mb="xl" color="white">Trusted by Creators</Title>
            <SimpleGrid cols={3} spacing="lg" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
              {featuredReviews.length > 0 ? (
                featuredReviews.map((review) => (
                  <Card key={review._id} style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid #333', color: 'white' }}>
                    <Text size="lg" style={{ fontStyle: 'italic' }}>"{review.comment}"</Text>
                    <GroupMantine position="center" mt="md" spacing="xs">
                      {/* You can add stars here if you want */}
                      <Text size="sm" color="dimmed">- {review.user}</Text>
                      <Badge size="xs" variant="outline" color="gray">{review.productId?.title}</Badge>
                    </GroupMantine>
                  </Card>
                ))
              ) : (
                ["Best quality I've seen.", "Super fast support.", "Love the drops!"].map((quote, i) => (
                  <Card key={i} style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid #333', color: 'white' }}><Text size="lg" style={{ fontStyle: 'italic' }}>"{quote}"</Text><Text size="sm" color="dimmed" mt="md">- User</Text></Card>
                ))
              )}
            </SimpleGrid>
          </GlassSection>
        </section>

        {/* FAQ SECTION (6) - NEW */}
        <section style={{ ...sectionStyle, paddingLeft: isMobile ? 0 : '10vw', justifyContent: isMobile ? 'center' : 'flex-start' }}>
          <GlassSection style={{ width: isMobile ? '90vw' : '50vw' }}>
            <Title order={2} mb="xl" color="white">Frequently Asked Questions</Title>
            <Accordion variant="separated" styles={{ item: { backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid #333' }, control: { color: 'white', '&:hover': { background: 'transparent' } }, content: { color: 'gray' }, chevron: { color: 'white' } }}>
              <Accordion.Item value="shipping">
                <Accordion.Control>How long does shipping take?</Accordion.Control>
                <Accordion.Panel>Usually 1-4 days for production updates, and 5-7 days for delivery depending on your location.</Accordion.Panel>
              </Accordion.Item>
              <Accordion.Item value="materials">
                <Accordion.Control>Is SLA  or SLS printing available?</Accordion.Control>
                <Accordion.Panel>Yes, all kind of 3d printing services are available including FDM,  SLA and SLS.</Accordion.Panel>
              </Accordion.Item>
              <Accordion.Item value="design">
                <Accordion.Control>Can you design a model for me?</Accordion.Control>
                <Accordion.Panel>Yes! Use our 'Design Enquiry' service to work with our 3D artists.</Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </GlassSection>
        </section>

        {/* FINAL SECTION: CTA + FOOTER (7) */}
        <section style={{ ...sectionStyle, flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Title size="3rem" style={{ color: 'white' }} mb="xl">Ready to Create?</Title>
            <Button component={Link} to="/upload-and-print" size="xl" variant="gradient" gradient={{ from: 'pink', to: 'orange' }}>Get Instant Quote</Button>
          </div>

          <div style={{ width: '100%', padding: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.8)' }}>
            <Container size="xl" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'gray' }}>
              <Text>© 2026 CDP3D. All rights reserved.</Text>
              <GroupMantine>
                <Link to="/about" style={{ color: 'gray', textDecoration: 'none', marginRight: '1rem' }}>About</Link>
                <Link to="/contact-us" style={{ color: 'gray', textDecoration: 'none', marginRight: '1rem' }}>Contact</Link>
                <Link to="/privacy-policy" style={{ color: 'gray', textDecoration: 'none' }}>Privacy</Link>
              </GroupMantine>
            </Container>
          </div>
        </section>

      </main>
    </div>
  );
};